"use client";

import React from "react";
import Link from "next/link";
import { Building2, ExternalLink, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface BaseStateProps {
  embedded: boolean;
}

function StateCard({ embedded, children }: BaseStateProps & { children: React.ReactNode }) {
  return (
    <Card className={embedded ? "border rounded-lg bg-white dark:bg-gray-900" : undefined}>
      <CardContent className="pt-6">{children}</CardContent>
    </Card>
  );
}

export function LoadingState({ embedded }: BaseStateProps) {
  return (
    <Card className={embedded ? "border rounded-lg bg-white dark:bg-gray-900" : undefined}>
      <CardContent className="pt-6 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading company…
      </CardContent>
    </Card>
  );
}

interface ErrorStateProps extends BaseStateProps {
  resolvedCompanyId: string | null;
  ticketId?: string | null;
}

export function ErrorState({ embedded, resolvedCompanyId, ticketId }: ErrorStateProps) {
  return (
    <Card className={embedded ? "border rounded-lg bg-white dark:bg-gray-900" : undefined}>
      <CardContent className="pt-6 space-y-3">
        <p className="text-sm text-destructive">Unable to load company details.</p>
        {resolvedCompanyId ? (
          <Link
            href={`/companies/${resolvedCompanyId}`}
            className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open company page
          </Link>
        ) : ticketId ? (
          <Link
            href={`/tickets/${ticketId}`}
            className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open linked ticket
          </Link>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function EmptyState({ embedded }: BaseStateProps) {
  return (
    <StateCard embedded={embedded}>
      <p className="text-sm text-gray-500">No company is linked to this entity.</p>
    </StateCard>
  );
}

interface DenormalizedStateProps extends BaseStateProps {
  headerTitle: string;
  companyName: string;
  ticketId?: string | null;
}

export function DenormalizedState({
  embedded,
  headerTitle,
  companyName,
  ticketId,
}: DenormalizedStateProps) {
  return (
    <div className={embedded ? "space-y-3" : "space-y-4"}>
      <div className="flex items-center gap-2">
        <Building2 className="w-5 h-5 text-gray-600 dark:text-gray-400 shrink-0" />
        <h3
          className={
            embedded
              ? "text-sm font-semibold text-gray-900 dark:text-gray-100"
              : "text-lg font-semibold text-card-foreground"
          }
        >
          {headerTitle}
        </h3>
      </div>
      <Card className={embedded ? "border bg-white dark:bg-gray-900" : undefined}>
        <CardContent className={embedded ? "pt-4 pb-4" : "pt-6"}>
          <p className="text-sm text-card-foreground">{companyName}</p>
          <p className="text-sm text-muted-foreground mt-2">
            This entry only stores the company name. Open the linked ticket to reach the full
            company record when available.
          </p>
          {ticketId ? (
            <Link
              href={`/tickets/${ticketId}`}
              className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mt-3"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              View ticket
            </Link>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
